import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, EMPTY, map, of, OperatorFunction } from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  SaveTemplatePayloadInput,
  TemplateActions,
} from '@ps/survey-template-data-access';
import {
  INITIAL_LOCATION_UPLOAD_STATE,
  SaveTemplateRequest,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import { SurveyAudienceForm } from '../../utils';
import { selectFeasibilityCpi, selectPricing } from '../insights';
import {
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';

import { selectSurveyFormState } from './survey-creation-audience.selectors';

/** A template write paired with the survey snapshot it will send. */
interface TemplateSaveContext<TRequest extends SaveTemplateRequest> {
  readonly request: TRequest;
  readonly input: SaveTemplatePayloadInput;
}

/**
 * Captures the survey on screen into the payload that saves it as a template,
 * or overwrites the one already applied to it.
 *
 * Only the capture lives here — the HTTP write sits with the other template
 * writes in `@ps/survey-template-data-access`, and these effects hand it the
 * finished payload on a `…Snapshot` action. The split is what keeps the
 * dependency one-way: this lib owns the survey form state, that lib cannot
 * read it without a cycle.
 */
@Injectable()
export class SurveyCreationTemplateSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  public readonly saveTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.saveTemplate),
      this._withTemplateSnapshot(),
      map(({ input }) => TemplateActions.saveTemplateSnapshot({ input })),
    ),
  );

  /**
   * `isServiceBuyer` comes off the request rather than the snapshot: it is the
   * template's own classification, and re-sending the current session's would
   * reclassify a template a differently-scoped buyer saved.
   */
  public readonly updateTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.updateTemplate),
      this._withTemplateSnapshot(),
      map(({ request, input }) =>
        TemplateActions.updateTemplateSnapshot({
          templateId: request.templateId,
          input: { ...input, isServiceBuyer: request.isServiceBuyer },
        }),
      ),
    ),
  );

  /**
   * Snapshots the live survey form into the template payload input.
   *
   * Drops the write when there is nothing valid to snapshot — no saved survey,
   * or an invalid form — after toasting the form's own error, exactly as
   * `SurveyCreationSaveEffects` does before a survey save. One pipeline rather
   * than a filter paired with throwing unwraps, so the gate and the narrowing
   * cannot drift apart.
   */
  private _withTemplateSnapshot<
    TAction extends { request: SaveTemplateRequest },
  >(): OperatorFunction<TAction, TemplateSaveContext<TAction['request']>> {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectSurveyFormState),
          this._store.select(selectPricing),
          this._store.select(selectFeasibilityCpi),
          this._store.select(selectSurvey),
        ]),
        concatMap(
          ([
            { request },
            surveyId,
            formState,
            pricingState,
            feasibilityCpi,
            survey,
          ]) => {
            const form = SurveyAudienceForm.from(formState);
            if (form.showErrors()) {
              Option.from(form.error).inspect((message) =>
                this._toastService.error(message),
              );
            }

            return Option.from(surveyId)
              .filter(() => form.valid && form.isStandardSurvey())
              .andThen((savedSurveyId) =>
                Option.from(form.data).map(
                  ({ metadata, qualifications, quotas }) => ({
                    request,
                    input: {
                      request,
                      surveyId: savedSurveyId,
                      survey: {
                        metadata,
                        userId: this._authSelectors.user().unwrap().id,
                        companyId: this._authSelectors.activeCompanyId(),
                        pricing: {
                          ...pricingState,
                          feasibilityCpi,
                        } satisfies SurveyPricingSnapshot,
                        targeting: {
                          qualifications,
                          quotas,
                          locationFileUpload: INITIAL_LOCATION_UPLOAD_STATE,
                        },
                        enableRespondentStatusFilter: false,
                      },
                      isFeotEnabled: Option.from(survey).mapOr(
                        false,
                        (loaded) => loaded.isFeotEnabled,
                      ),
                      isServiceBuyer:
                        this._authSelectors.hasImpersonatedBuyer(),
                    } satisfies SaveTemplatePayloadInput,
                  }),
                ),
              )
              .match({
                some: (context) => of(context),
                none: () => EMPTY,
              });
          },
        ),
      );
  }
}
